const SYNC_TAG = "eventsapp-sync-queue";

let hasBoundMessageListener = false;
let externalMessageHandler = null;

const dispatchSyncEvent = (detail) => {
  window.dispatchEvent(new CustomEvent("pwa-sync-update", { detail }));
  if (typeof externalMessageHandler === "function") {
    externalMessageHandler(detail);
  }
};

const requestReplay = async (registration) => {
  if (!registration) {
    return;
  }

  if (registration.sync) {
    try {
      await registration.sync.register(SYNC_TAG);
      return;
    } catch (error) {
      // Ignore and fall back to direct replay message.
    }
  }

  if (registration.active) {
    registration.active.postMessage({ type: "REPLAY_SYNC_QUEUE" });
  }
};

const bindWorkerMessageListener = () => {
  if (hasBoundMessageListener) {
    return;
  }

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (!event.data || typeof event.data !== "object") {
      return;
    }
    dispatchSyncEvent(event.data);
  });

  hasBoundMessageListener = true;
};

const postMessageToWorker = (registration, message) => {
  if (!registration || !message) {
    return;
  }

  const target = registration.active || registration.waiting || registration.installing;
  if (target) {
    target.postMessage(message);
  }
};

const requestWorkerVersion = (registration) => {
  postMessageToWorker(registration, { type: "GET_SW_VERSION" });
};

export const registerServiceWorker = ({ onWorkerMessage } = {}) => {
  externalMessageHandler = typeof onWorkerMessage === "function" ? onWorkerMessage : null;

  if (!("serviceWorker" in navigator) || process.env.REACT_APP_DISABLE_SW === "true") {
    return;
  }

  if (!window.isSecureContext && window.location.hostname !== "localhost") {
    console.warn(
      "Service worker is disabled on non-secure origins. Use HTTPS or localhost for offline PWA testing.",
    );
    return;
  }

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      bindWorkerMessageListener();
      requestWorkerVersion(registration);

      if (navigator.onLine) {
        await requestReplay(registration);
      }

      window.addEventListener("online", () => {
        requestReplay(registration);
        requestWorkerVersion(registration);
      });

      registration.addEventListener("updatefound", () => {
        dispatchSyncEvent({ type: "SW_UPDATE_FOUND" });
      });
    } catch (error) {
      // Do not crash app startup if worker fails to register.
      console.error("Service worker registration failed", error);
    }
  });
};

export const triggerSyncReplay = async () => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  await requestReplay(registration);
};
